/**
 * The periods a report offers, as one ordered catalogue.
 *
 * `reporting-period.ts` holds the arithmetic; this holds the *menu* — which
 * periods a reader may pick, what each is called, and how a stored choice is
 * read back. Kept apart because the arithmetic is worth testing without
 * agreeing on the wording, and the wording changes more often than the maths.
 *
 * The order here is the order on screen: today, then yesterday, then the
 * to-date periods, because a reader checking the period they are in asks more
 * often than one closing a finished one; then the completed periods; then the
 * rolling windows that match what the ACH returns report already offers; then
 * the hand-picked range.
 *
 * Every preset resolves against an injected `reference` day rather than reading
 * the clock, which is what lets a test pin "today" and assert a real date. The
 * clock is supplied one layer up, by `useReportScope`.
 *
 * `choosePeriod` and `chooseWeekStart` hold what happens when a reader presses
 * the control. They are here, and pure, because two stores keep a period: a
 * table report keeps it in the URL and a chart screen in React state. The rule
 * must not be written twice — that is the drift ADR-0011 exists to stop.
 */

import { formatCalendarDate, type CalendarDateRange } from './calendar-date.ts'
import {
  completedWeek,
  lastCalendarYear,
  monthToDate,
  previousCalendarMonth,
  previousCalendarQuarter,
  previousDay,
  quarterToDate,
  rollingDays,
  weekToDate,
  yearToDate,
  type WeekStart
} from './reporting-period.ts'

/**
 * A period that names its own days.
 *
 * Every preset but `custom`. The distinction is a type rather than a comment
 * because a caller holding one of these needs no null check — see the
 * `resolvePeriod` overloads.
 */
export type NamedPeriodPresetId =
  | 'today'
  | 'yesterday'
  | 'weekToDate'
  | 'monthToDate'
  | 'quarterToDate'
  | 'yearToDate'
  | 'lastWeek'
  | 'lastMonth'
  | 'lastQuarter'
  | 'lastYear'
  | 'last7'
  | 'last30'
  | 'last90'
  | 'last180'

/** A period defined by the week, and so moved by a changed week start. */
export type WeekPeriodPresetId = 'weekToDate' | 'lastWeek'

/** Every period a report offers, including the hand-picked one. */
export type PeriodPresetId = NamedPeriodPresetId | 'custom'

/**
 * One entry in the menu.
 *
 * `resolve` is absent for `custom` alone: that period is whatever the reader
 * picked on the calendar, so there is nothing to compute. A caller that treats
 * a missing `resolve` as "leave the range alone" needs no special case for it.
 */
export interface PeriodPreset {
  id: PeriodPresetId
  label: string
  resolve?: (reference: Date, weekStart: WeekStart) => CalendarDateRange
}

/**
 * The menu, in the order it is offered.
 *
 * Frozen because it is read by every report; a screen that sorted or spliced it
 * in place would reorder the control on every other screen.
 */
export const PERIOD_PRESETS: readonly PeriodPreset[] = Object.freeze([
  {
    id: 'today',
    label: 'Today',
    resolve: reference => rollingDays(1, reference)
  },
  { id: 'yesterday', label: 'Yesterday', resolve: previousDay },
  { id: 'weekToDate', label: 'Week to date', resolve: weekToDate },
  { id: 'monthToDate', label: 'Month to date', resolve: monthToDate },
  { id: 'quarterToDate', label: 'Quarter to date', resolve: quarterToDate },
  { id: 'yearToDate', label: 'Year to date', resolve: yearToDate },
  {
    id: 'lastWeek',
    label: 'Last completed week',
    resolve: (reference, weekStart) => completedWeek(reference, weekStart)
  },
  {
    id: 'lastMonth',
    label: 'Last calendar month',
    resolve: previousCalendarMonth
  },
  {
    id: 'lastQuarter',
    label: 'Last calendar quarter',
    resolve: previousCalendarQuarter
  },
  { id: 'lastYear', label: 'Last calendar year', resolve: lastCalendarYear },
  {
    id: 'last7',
    label: 'Last 7 days',
    resolve: reference => rollingDays(7, reference)
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    resolve: reference => rollingDays(30, reference)
  },
  {
    id: 'last90',
    label: 'Last 90 days',
    resolve: reference => rollingDays(90, reference)
  },
  {
    id: 'last180',
    label: 'Last 180 days',
    resolve: reference => rollingDays(180, reference)
  },
  { id: 'custom', label: 'Custom range' }
])

/**
 * The view parameters the period control owns.
 *
 * They are in the URL for the reader, not for the endpoint: a report is scoped
 * by the `from` and `to` its period resolves to, and no endpoint here knows the
 * word `lastQuarter`. A table's `toSearchParams` therefore omits both, the same
 * way Retry Analysis omits the `code` that says which dialog is open. Sending
 * them would be an undeclared parameter, which is a 400 rather than a silent
 * ignore — root AGENTS.md § Conventions.
 */
export const PERIOD_VIEW_PARAMS: readonly string[] = Object.freeze([
  'period',
  'weekStart'
])

/**
 * Read a preset back off a control or a URL.
 *
 * Unknown ids return null rather than the first preset, so a stale link cannot
 * quietly show a different period than the one it names.
 *
 * @param value - A candidate preset id.
 * @returns The preset id, or null when the value names none.
 */
export function parsePeriodPresetId(value: unknown): PeriodPresetId | null {
  return PERIOD_PRESETS.find(preset => preset.id === value)?.id ?? null
}

/**
 * The day range a preset means.
 *
 * Overloaded on the id: a named period always has a range, so a caller holding
 * one is spared a null check it could only answer with a second default. Only
 * `custom` widens the result, and only because there is nothing to compute.
 *
 * @param id - Which preset.
 * @param reference - The day "now" falls on.
 * @param weekStart - The day the week begins.
 * @returns The range, or null for `custom`.
 */
export function resolvePeriod(
  id: NamedPeriodPresetId,
  reference: Date,
  weekStart: WeekStart
): CalendarDateRange
export function resolvePeriod(
  id: PeriodPresetId,
  reference: Date,
  weekStart: WeekStart
): CalendarDateRange | null
export function resolvePeriod(
  id: PeriodPresetId,
  reference: Date,
  weekStart: WeekStart
): CalendarDateRange | null {
  const preset = PERIOD_PRESETS.find(candidate => candidate.id === id)

  return preset?.resolve ? preset.resolve(reference, weekStart) : null
}

/**
 * Whether a preset's range moves when the week start changes.
 *
 * Only the two week periods do. A screen uses this to leave a month or a
 * rolling window alone when the reader changes the week convention, rather than
 * recomputing a range that would come back identical.
 *
 * It narrows rather than returning a bare boolean, so the caller that acts on a
 * true answer can resolve the period without a null check.
 *
 * @param id - Which preset.
 * @returns True when the period is defined by the week.
 */
export function dependsOnWeekStart(
  id: PeriodPresetId
): id is WeekPeriodPresetId {
  return id === 'weekToDate' || id === 'lastWeek'
}

/** A period a report is showing, and the days it covers. */
export interface PeriodChoice {
  period: PeriodPresetId
  weekStart: WeekStart
  range: CalendarDateRange
}

/**
 * The choice after a reader picks a period from the menu.
 *
 * Picking `custom` deliberately keeps the range already on screen rather than
 * resetting it. Being thrown back to a default is the opposite of what that
 * choice promises, and the calendar is what the reader reaches for next.
 *
 * @param current - The choice in force.
 * @param next - The period the reader picked.
 * @param reference - The day "now" falls on.
 * @returns The choice to show.
 */
export function choosePeriod(
  current: PeriodChoice,
  next: PeriodPresetId,
  reference: Date
): PeriodChoice {
  // `custom` resolves to null, which is how the reader keeps their range.
  const range =
    resolvePeriod(next, reference, current.weekStart) ?? current.range

  return { period: next, weekStart: current.weekStart, range }
}

/**
 * The choice after a reader changes which day the week begins on.
 *
 * Only a week period moves. Recomputing a month here would hand the screen an
 * identical range and refetch the report for nothing.
 *
 * @param current - The choice in force.
 * @param next - The day the week now begins on.
 * @param reference - The day "now" falls on.
 * @returns The choice to show.
 */
export function chooseWeekStart(
  current: PeriodChoice,
  next: WeekStart,
  reference: Date
): PeriodChoice {
  const range = dependsOnWeekStart(current.period)
    ? resolvePeriod(current.period, reference, next)
    : current.range

  return { period: current.period, weekStart: next, range }
}

/**
 * The choice after a reader edits the calendar.
 *
 * The period becomes `custom`, because the days no longer come from the menu.
 * A control that left a named period standing over a hand-picked range would
 * label the report as something it is not.
 *
 * @param current - The choice in force.
 * @param next - The range the reader picked.
 * @returns The choice to show.
 */
export function chooseRange(
  current: PeriodChoice,
  next: CalendarDateRange
): PeriodChoice {
  return { period: 'custom', weekStart: current.weekStart, range: next }
}

/**
 * The period line a read-only screen shows.
 *
 * A named period uses its menu label. A hand-picked range uses its two days,
 * because "Custom range" does not say which days the report covers.
 *
 * @param choice - The period in force.
 * @returns The line.
 */
export function periodScopeLabel(choice: PeriodChoice): string {
  const preset = PERIOD_PRESETS.find(
    candidate => candidate.id === choice.period
  )
  if (choice.period !== 'custom' && preset !== undefined) {
    return preset.label
  }

  const start = formatCalendarDate(choice.range.startDate)
  const end = formatCalendarDate(choice.range.endDate)
  if (start !== '' && end !== '') {
    return `${start} – ${end}`
  }

  return preset?.label ?? 'Custom range'
}
